import React, { useEffect, useMemo, useState } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import ToolkitProvider, { CSVExport } from "react-bootstrap-table2-toolkit";
import { Card, CardBody } from "reactstrap";
import { MPC_COPY } from "./monthlyPhoneConsultationLabels";

const { ExportCSVButton } = CSVExport;

const ROLE_FILTER_ALL = "all";

const ROLE_FILTER_OPTIONS = [
  { value: ROLE_FILTER_ALL, labelKey: "teamRoleFilterAll" },
  { value: "staff", labelKey: "teamRoleFilterStaff" },
  { value: "careprovider", labelKey: "teamRoleFilterProvider" }
];

const formatRoleLabel = (role) => {
  if (role === "careprovider") return "Provider";
  if (role === "staff") return "Staff";
  return "Member";
};

const normalizeMemberRole = (role) => {
  if (role === "staff" || role === "careprovider") {
    return role;
  }
  return "member";
};

const ColumnHeader = ({ title, subtitle }) => (
  <div className="mpc-team-column-header">
    <span className="mpc-team-column-header-title">{title}</span>
    {subtitle ? <span className="mpc-team-column-header-sub">{subtitle}</span> : null}
  </div>
);

const capitalizeFirstLetter = (value) => {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
};

const TeamMemberProfileCell = ({ name, email, roleLabel, isActive }) => (
  <div className="mpr-patient-profile-cell">
    <div className="mpr-patient-profile-avatar-wrap">
      <span className="mpr-patient-profile-fallback-icon">
        <i className={`mdi ${roleLabel === "Provider" ? "mdi-stethoscope" : "mdi-account-tie"}`} />
      </span>
    </div>
    <div className="mpr-patient-profile-content">
      <div className="mpr-patient-profile-name-row">
        <div className="mpr-patient-profile-name">{capitalizeFirstLetter(name) || "-"}</div>
        <span className={`mpr-team-role-tag ${roleLabel === "Provider" ? "provider" : "staff"}`}>{roleLabel}</span>
        <span className={`mpr-team-status-tag ${isActive ? "active" : "inactive"}`}>
          {isActive ? "Active" : "Inactive"}
        </span>
      </div>
      <div className="mpr-patient-profile-meta">
        <i className="mdi mdi-email-outline" />
        <span>{email || "-"}</span>
      </div>
    </div>
  </div>
);

const getMonthNameFromLabel = (monthLabel) => {
  if (!monthLabel) {
    return "previous month";
  }
  return String(monthLabel).trim().split(/\s+/)[0];
};

const formatPriorMonthsList = (priorThreeMonths = []) =>
  priorThreeMonths
    .map((month) => month?.label)
    .filter(Boolean)
    .join(" · ");

const formatTrendComparisonMonth = (monthLabel) => monthLabel || "the previous month";

const TrendCell = ({ value, current = 0, previous = 0, currentMonthLabel, previousMonthLabel }) => {
  const change = Number(value) || 0;
  const comparisonMonth = formatTrendComparisonMonth(previousMonthLabel);
  const selectedMonth = formatTrendComparisonMonth(currentMonthLabel);

  if (change > 0) {
    return (
      <div className="mpc-team-trend-cell mpc-team-trend-cell--up">
        <i className="mdi mdi-trending-up" aria-hidden="true" />
        <span>+{change}</span>
        <small>
          more in {selectedMonth} than {comparisonMonth}
        </small>
      </div>
    );
  }
  if (change < 0) {
    return (
      <div className="mpc-team-trend-cell mpc-team-trend-cell--down">
        <i className="mdi mdi-trending-down" aria-hidden="true" />
        <span>{Math.abs(change)}</span>
        <small>
          fewer in {selectedMonth} than {comparisonMonth}
        </small>
      </div>
    );
  }
  if (Number(current) === 0 && Number(previous) === 0) {
    return (
      <div className="mpc-team-trend-cell mpc-team-trend-cell--flat">
        <span>—</span>
        <small>
          no activity in {selectedMonth} or {comparisonMonth}
        </small>
      </div>
    );
  }
  return (
    <div className="mpc-team-trend-cell mpc-team-trend-cell--flat">
      <i className="mdi mdi-minus" aria-hidden="true" />
      <span>Same</span>
      <small>
        in {selectedMonth} as {comparisonMonth}
      </small>
    </div>
  );
};

const CountCell = ({ value, emphasize = false, onClick, clickable = false, noteCountLabel = "notes" }) => {
  const count = Number(value) || 0;
  const canClick = clickable && count > 0 && typeof onClick === "function";
  const className = `mpc-team-count-cell ${emphasize ? "mpc-team-count-cell--emphasize" : ""} ${
    canClick ? "mpc-team-count-cell--clickable" : ""
  }`;

  if (canClick) {
    return (
      <button
        type="button"
        className={`mpc-team-count-btn ${className}`}
        onClick={onClick}
        title={`View patients for ${count} documented consultation ${noteCountLabel}`}
        aria-label={`View ${count} documented consultation ${noteCountLabel}`}
      >
        <span className="mpc-team-count-value">{count}</span>
        <small className="mpc-team-count-type">{noteCountLabel}</small>
      </button>
    );
  }

  return (
    <div className={className}>
      <span className="mpc-team-count-value">{count}</span>
      <small className="mpc-team-count-type">{noteCountLabel}</small>
    </div>
  );
};

const AverageCell = ({ value, priorCounts = [], priorMonths = [], notesPerMonthLabel = "notes per month" }) => {
  const average = Number(value);
  const display = Number.isFinite(average) ? average.toFixed(1) : "0.0";
  const counts = Array.isArray(priorCounts) ? priorCounts : [];
  const hasNamedBreakdown = priorMonths.length === 3 && counts.length === 3;
  const namedParts = hasNamedBreakdown
    ? priorMonths.map((month, index) => {
        const monthName = getMonthNameFromLabel(month?.label);
        return `${monthName} ${counts[index] ?? 0}`;
      })
    : [];
  const breakdownText = hasNamedBreakdown
    ? `(${namedParts.join(" + ")}) ÷ 3 = ${display}`
    : null;

  return (
    <div className="mpc-team-count-cell mpc-team-average-cell">
      <span className="mpc-team-count-value">{display}</span>
      <small className="mpc-team-count-hint">{notesPerMonthLabel}</small>
      {hasNamedBreakdown ? (
        <small
          className="mpc-team-average-breakdown"
          title={priorMonths.map((month, index) => `${month?.label || `Month ${index + 1}`}: ${counts[index] ?? 0} notes`).join(", ")}
        >
          {breakdownText}
        </small>
      ) : null}
    </div>
  );
};

const ThreeMonthAveragePanel = ({ priorThreeMonths = [], average, priorCounts = [], mpcCopy = MPC_COPY }) => {
  if (!priorThreeMonths.length) {
    return null;
  }

  const counts = priorCounts.length === 3
    ? priorCounts
    : priorThreeMonths.map((month) => month.teamTotal ?? 0);
  const averageDisplay = Number.isFinite(Number(average)) ? Number(average).toFixed(1) : "0.0";
  const monthSublabel = mpcCopy.teamThreeMonthAverageMonthSublabel || "consultation notes";

  return (
    <div className="mpc-team-average-panel">
      <div className="mpc-team-average-panel-title">
        <i className="mdi mdi-calculator-variant-outline" aria-hidden="true" />
        <span>{mpcCopy.teamThreeMonthAverageTitle || "3-Month Average — Consultation Notes"}</span>
      </div>
      <p className="mpc-team-average-panel-lead">
        {mpcCopy.teamThreeMonthAverageLead || mpcCopy.teamUsualPaceHint}. The selected month is{" "}
        <strong>not</strong> included.
      </p>
      <div className="mpc-team-average-panel-months">
        {priorThreeMonths.map((month, index) => (
          <div key={month.label || index} className="mpc-team-average-panel-month">
            <span className="mpc-team-average-panel-month-label">{month.label}</span>
            <strong>{counts[index] ?? month.teamTotal ?? 0}</strong>
            <span className="mpc-team-average-panel-month-sublabel">{monthSublabel}</span>
          </div>
        ))}
      </div>
      <div className="mpc-team-average-panel-formula">
        (
        {priorThreeMonths
          .map((month, index) => `${getMonthNameFromLabel(month?.label)} ${counts[index] ?? 0}`)
          .join(" + ")}
        ) ÷ 3 = <strong>{averageDisplay}</strong>{" "}
        {mpcCopy.teamConsultationNotesPerMonthLabel || "consultation notes per month"}
      </div>
    </div>
  );
};

const getComparisonMonthName = getMonthNameFromLabel;

const buildTeamInsight = (currentTotal, previousTotal, currentMonthLabel, previousMonthLabel, consultationTitle) => {
  const current = Number(currentTotal) || 0;
  const previous = Number(previousTotal) || 0;
  const diff = current - previous;
  const comparisonMonth = getComparisonMonthName(previousMonthLabel);
  const noteLabel = current === 1 ? "note" : "notes";

  if (current === 0 && previous === 0) {
    return {
      tone: "neutral",
      icon: "mdi-information-outline",
      message: `No ${consultationTitle} notes were documented in ${currentMonthLabel} or ${previousMonthLabel || "the month before"}.`
    };
  }

  if (diff > 0) {
    return {
      tone: "up",
      icon: "mdi-trending-up",
      message: `The team documented ${current} ${consultationTitle} ${noteLabel} in ${currentMonthLabel} — ${diff} more than ${comparisonMonth} month (${previous}).`
    };
  }

  if (diff < 0) {
    return {
      tone: "down",
      icon: "mdi-trending-down",
      message: `The team documented ${current} ${consultationTitle} ${noteLabel} in ${currentMonthLabel} — ${Math.abs(diff)} fewer than ${comparisonMonth} month (${previous}).`
    };
  }

  return {
    tone: "flat",
    icon: "mdi-equal",
    message: `The team documented ${current} ${consultationTitle} ${noteLabel} in ${currentMonthLabel} — the same as ${comparisonMonth} month (${previous}).`
  };
};

const StaffPerformanceSection = ({ staffPerformance, embedded = false, onTeamMemberMonthClick, mpcCopy = MPC_COPY, programType = "rpm" }) => {
  const [searchText, setSearchText] = useState("");
  const [roleFilter, setRoleFilter] = useState(ROLE_FILTER_ALL);

  const members = staffPerformance?.members || [];
  const currentMonthLabel = staffPerformance?.currentMonth?.label || "Selected month";
  const previousMonthLabel = staffPerformance?.previousMonth?.label || "Previous month";
  const threeMonthRangeLabel = staffPerformance?.threeMonthAverageLabel || "Prior 3 months";
  const priorThreeMonths = staffPerformance?.priorThreeMonths || [];

  useEffect(() => {
    setRoleFilter(ROLE_FILTER_ALL);
    setSearchText("");
  }, [currentMonthLabel, members.length]);

  const priorMonthsListLabel = useMemo(
    () => formatPriorMonthsList(priorThreeMonths),
    [priorThreeMonths]
  );

  const tableMonthPeriods = useMemo(() => {
    if (staffPerformance?.tableMonthPeriods?.length) {
      return staffPerformance.tableMonthPeriods;
    }

    return [
      {
        key: "month2Back",
        dataField: "month2BackCount",
        label: priorThreeMonths[1]?.label || "Two months ago",
        subtitle: mpcCopy.teamMonthColumnSubtitle
      },
      {
        key: "previous",
        dataField: "month1BackCount",
        label: previousMonthLabel,
        subtitle: mpcCopy.teamMonthColumnSubtitle
      },
      {
        key: "current",
        dataField: "currentMonthCount",
        label: currentMonthLabel,
        subtitle: mpcCopy.teamMonthColumnSubtitle,
        emphasize: true
      }
    ];
  }, [staffPerformance?.tableMonthPeriods, priorThreeMonths, previousMonthLabel, currentMonthLabel, mpcCopy.teamMonthColumnSubtitle]);

  const tableMonthsListLabel = useMemo(
    () => tableMonthPeriods.map((period) => period.label).join(" · "),
    [tableMonthPeriods]
  );

  const rows = useMemo(
    () =>
      members.map((member) => {
        const role = normalizeMemberRole(member.role);
        const priorCounts = Array.isArray(member.priorThreeMonthCounts) ? member.priorThreeMonthCounts : [];

        return {
          ...member,
          _id: member.userId,
          role,
          roleLabel: formatRoleLabel(role),
          month2BackCount: priorCounts[1] ?? 0,
          month1BackCount: member.previousMonthCount ?? priorCounts[0] ?? 0
        };
      }),
    [members]
  );

  const roleCounts = useMemo(() => {
    const counts = {
      [ROLE_FILTER_ALL]: rows.length,
      staff: 0,
      careprovider: 0
    };

    rows.forEach((row) => {
      if (row.role === "staff") {
        counts.staff += 1;
      } else if (row.role === "careprovider") {
        counts.careprovider += 1;
      }
    });

    return counts;
  }, [rows]);

  const roleFilterOptions = useMemo(
    () =>
      ROLE_FILTER_OPTIONS.filter(
        (option) => option.value === ROLE_FILTER_ALL || roleCounts[option.value] > 0
      ),
    [roleCounts]
  );

  useEffect(() => {
    if (roleFilter !== ROLE_FILTER_ALL && !roleCounts[roleFilter]) {
      setRoleFilter(ROLE_FILTER_ALL);
    }
  }, [roleCounts, roleFilter]);

  const filteredRows = useMemo(() => {
    let result = rows;

    if (roleFilter !== ROLE_FILTER_ALL) {
      result = result.filter((row) => row.role === roleFilter);
    }

    const query = searchText.toLowerCase().trim();
    if (!query) {
      return result;
    }

    result = result.filter((row) =>
      [row.name, row.email, row.roleLabel]
        .map((value) => String(value || "").toLowerCase())
        .some((value) => value.includes(query))
    );

    return result;
  }, [rows, searchText, roleFilter]);

  const sortedRows = useMemo(() => {
    return [...filteredRows].sort((a, b) => {
      const countDiff = (Number(b.currentMonthCount) || 0) - (Number(a.currentMonthCount) || 0);
      if (countDiff !== 0) {
        return countDiff;
      }
      return String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" });
    });
  }, [filteredRows]);

  const columns = useMemo(() => {
    const monthColumns = tableMonthPeriods.map((period) => ({
      dataField: period.dataField,
      text: period.label,
      sort: true,
      headerFormatter: () => <ColumnHeader title={period.label} subtitle={period.subtitle} />,
      headerClasses: period.emphasize ? "mpc-staff-col-current" : "mpc-staff-col-prior-month",
      classes: period.emphasize ? "mpc-staff-col-current" : "mpc-staff-col-prior-month",
      headerStyle: () => ({ minWidth: "120px" }),
      formatter: (value, row) => (
        <CountCell
          value={value}
          emphasize={period.emphasize}
          clickable={Boolean(onTeamMemberMonthClick && period.startDate && period.endDate)}
          noteCountLabel={mpcCopy.teamTableNoteCountLabel}
          onClick={() =>
            onTeamMemberMonthClick?.({
              teamMemberUserId: row.userId,
              teamMemberName: row.name,
              monthLabel: period.label,
              startDate: period.startDate,
              endDate: period.endDate
            })
          }
        />
      )
    }));

    return [
      {
        dataField: "name",
        text: "Team Member",
        sort: true,
        formatter: (value, row) => (
          <TeamMemberProfileCell name={value} email={row.email} roleLabel={row.roleLabel} isActive={row.isActive} />
        ),
        headerStyle: () => ({ minWidth: "260px" })
      },
      ...monthColumns,
      {
        dataField: "monthOverMonthChange",
        text: mpcCopy.teamTrend,
        sort: true,
        headerFormatter: () => (
          <ColumnHeader
            title={mpcCopy.teamTrend}
            subtitle={`${currentMonthLabel} vs ${previousMonthLabel}`}
          />
        ),
        headerStyle: () => ({ minWidth: "170px" }),
        formatter: (value, row) => (
          <TrendCell
            value={value}
            current={row.currentMonthCount}
            previous={row.month1BackCount}
            currentMonthLabel={currentMonthLabel}
            previousMonthLabel={previousMonthLabel}
          />
        )
      },
      {
        dataField: "threeMonthAverage",
        text: mpcCopy.teamUsualPace,
        sort: true,
        headerFormatter: () => (
          <ColumnHeader
            title={mpcCopy.teamUsualPace}
            subtitle={priorMonthsListLabel || threeMonthRangeLabel.replace(/^Avg\s*\(/i, "").replace(/\)$/, "")}
          />
        ),
        headerStyle: () => ({ minWidth: "150px" }),
        formatter: (value, row) => (
          <AverageCell
            value={value}
            priorCounts={row.priorThreeMonthCounts}
            priorMonths={priorThreeMonths}
            notesPerMonthLabel={mpcCopy.teamConsultationNotesPerMonthLabel}
          />
        )
      }
    ];
  }, [
    tableMonthPeriods,
    currentMonthLabel,
    previousMonthLabel,
    priorMonthsListLabel,
    priorThreeMonths,
    threeMonthRangeLabel,
    onTeamMemberMonthClick,
    mpcCopy
  ]);

  const customTotal = (from, to, size) => (
    <span className="react-bootstrap-table-pagination-total">
      Showing {from} to {to} of {size} team members
    </span>
  );

  const paginationOptions = useMemo(() => ({
    bootstrap4: true,
    sizePerPage: 10,
    totalSize: sortedRows.length,
    page: 1,
    firstPageText: "First",
    prePageText: "Back",
    nextPageText: "Next",
    lastPageText: "Last",
    alwaysShowAllBtns: true,
    withFirstAndLast: true,
    hideSizePerPage: false,
    hidePageListOnlyOnePage: true,
    showTotal: sortedRows.length > 0,
    paginationTotalRenderer: customTotal,
    sizePerPageList: [
      { text: "10", value: 10 },
      { text: "25", value: 25 },
      { text: "50", value: 50 }
    ]
  }), [sortedRows.length]);

  const paginationTableKey = useMemo(
    () => `${searchText.trim().toLowerCase()}|${roleFilter}|${sortedRows.length}`,
    [searchText, roleFilter, sortedRows.length]
  );

  if (!staffPerformance) {
    return (
      <div className="mpc-empty-state mpc-team-performance-empty">
        <i className="mdi mdi-account-supervisor-outline" />
        <p>Team performance data is not available for this period.</p>
      </div>
    );
  }

  const currentTotal = staffPerformance?.totals?.currentMonthCount ?? 0;
  const previousTotal = staffPerformance?.totals?.previousMonthCount ?? 0;
  const usualPace = Number(staffPerformance?.totals?.threeMonthAverage);
  const priorThreeMonthTotals = staffPerformance?.totals?.priorThreeMonthCounts || [];
  const insight = buildTeamInsight(
    currentTotal,
    previousTotal,
    currentMonthLabel,
    previousMonthLabel,
    mpcCopy.consultationTitle
  );
  const usualPaceDisplay = Number.isFinite(usualPace) ? usualPace.toFixed(1) : "0.0";

  const panelBody = (
    <>
      <div className="mpc-team-intro-row">
        <div className="mpc-panel-intro mpc-team-intro-panel">
          <div className="mpc-panel-intro-row">
            <div className="mpc-panel-intro-copy">
              <h4>Who Documented Consultation Notes?</h4>
              <p>{mpcCopy.teamPerformanceIntro}</p>
            </div>
            <span className="mpc-list-count-badge">
              {sortedRows.length} team {sortedRows.length === 1 ? "member" : "members"}
            </span>
          </div>
        </div>

        <div className={`mpc-team-insight-banner mpc-team-insight-banner--${insight.tone}`}>
          <i className={`mdi ${insight.icon}`} aria-hidden="true" />
          <p>{insight.message}</p>
        </div>
      </div>

      <div className="mpc-team-stat-row">
        <div className="mpc-team-stat-card mpc-team-stat-card--current">
          <div className="mpc-team-stat-card-head">
            <i className="mdi mdi-calendar-check" aria-hidden="true" />
            <span className="mpc-team-stat-label">{currentMonthLabel}</span>
          </div>
          <strong className="mpc-team-stat-value">{currentTotal}</strong>
          <span className="mpc-team-stat-caption">Selected month</span>
          <span className="mpc-team-stat-note">{mpcCopy.teamStatNoteFootnote}</span>
        </div>
        <div className="mpc-team-stat-card">
          <div className="mpc-team-stat-card-head">
            <i className="mdi mdi-calendar-clock" aria-hidden="true" />
            <span className="mpc-team-stat-label">{previousMonthLabel}</span>
          </div>
          <strong className="mpc-team-stat-value">{previousTotal}</strong>
          <span className="mpc-team-stat-caption">Previous month</span>
          <span className="mpc-team-stat-note">{mpcCopy.teamStatNoteFootnote}</span>
        </div>
        <div className="mpc-team-stat-card">
          <div className="mpc-team-stat-card-head">
            <i className="mdi mdi-chart-timeline-variant" aria-hidden="true" />
            <span className="mpc-team-stat-label">{mpcCopy.teamUsualPace}</span>
          </div>
          <strong className="mpc-team-stat-value">{usualPaceDisplay}</strong>
          <span className="mpc-team-stat-caption">{priorMonthsListLabel || threeMonthRangeLabel}</span>
          <span className="mpc-team-stat-footnote">{mpcCopy.teamConsultationNotesPerMonthLabel}</span>
          <span className="mpc-team-stat-note">{mpcCopy.teamStatNoteFootnote}</span>
        </div>
      </div>

      <ThreeMonthAveragePanel
        priorThreeMonths={priorThreeMonths}
        average={usualPace}
        priorCounts={priorThreeMonthTotals}
        mpcCopy={mpcCopy}
      />

      <div className="mpc-team-table-section-head">
        <h5>{mpcCopy.teamTableSectionTitle}</h5>
        <p>
          {mpcCopy.teamTableSectionSubtitle} {onTeamMemberMonthClick ? mpcCopy.teamCountClickHint : ""}
        </p>
        {tableMonthsListLabel ? (
          <div className="mpc-team-table-period-legend" aria-label="Table month periods">
            <span>
              {mpcCopy.teamTablePeriodLegendPrefix}{" "}
              <strong>{tableMonthsListLabel}</strong>
            </span>
          </div>
        ) : null}
      </div>

      <div className="mpr-patient-card mpc-patient-table-wrap mpc-team-table-wrap">
        <ToolkitProvider
          keyField="_id"
          data={sortedRows}
          columns={columns}
          exportCSV={{
            fileName: `monthly-phone-consultation-${programType}-team-${currentMonthLabel.replace(/\s+/g, "-")}.csv`,
            exportAll: true,
            onlyExportFiltered: true
          }}
        >
          {(toolkitProps) => (
            <>
              <div className="mpr-patient-toolbar patient-list-toolbar mpc-team-toolbar">
                <div className="mpc-team-toolbar-controls">
                  <div className="mpr-search-wrap patient-list-search-shell">
                    <span className="patient-list-search-icon">
                      <i className="mdi mdi-magnify" />
                    </span>
                    <input
                      type="text"
                      className="patient-list-search-input"
                      placeholder="Search by name or email..."
                      value={searchText}
                      onChange={(event) => setSearchText(event.target.value)}
                    />
                    {searchText && (
                      <button
                        type="button"
                        className="patient-list-search-clear"
                        onClick={() => setSearchText("")}
                        aria-label="Clear search"
                      >
                        <i className="mdi mdi-close-circle" />
                      </button>
                    )}
                  </div>

                  {roleFilterOptions.length > 1 && (
                    <div className="mpc-team-role-filter-wrap">
                      <span className="mpc-team-role-filter-label">{mpcCopy.teamRoleFilterLabel}</span>
                      <div className="mpc-team-role-filter" role="group" aria-label="Filter by role">
                        {roleFilterOptions.map((option) => {
                          const isActive = roleFilter === option.value;
                          const count = roleCounts[option.value] ?? 0;
                          return (
                            <button
                              key={option.value}
                              type="button"
                              className={`mpc-team-role-filter-btn ${isActive ? "active" : ""} ${
                                option.value === "staff"
                                  ? "staff"
                                  : option.value === "careprovider"
                                    ? "provider"
                                    : "all"
                              }`}
                              aria-pressed={isActive}
                              onClick={() => setRoleFilter(option.value)}
                            >
                              <span>{mpcCopy[option.labelKey]}</span>
                              <span className="mpc-team-role-filter-count">{count}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {sortedRows.length > 0 && (
                  <ExportCSVButton {...toolkitProps.csvProps} className="mpr-export-btn patient-list-export-btn">
                    <span className="patient-list-export-icon">
                      <i className="mdi mdi-file-export-outline" />
                    </span>
                    <span>Export CSV</span>
                  </ExportCSVButton>
                )}
              </div>
              <div className="mpc-table-scroll-shell">
                <BootstrapTable
                  key={paginationTableKey}
                  {...toolkitProps.baseProps}
                  bootstrap4
                  bordered={false}
                  wrapperClasses="table-responsive mpc-table-responsive"
                  classes="mpr-patient-table mpc-patient-table mpc-team-table"
                  defaultSorted={[
                    {
                      dataField: "currentMonthCount",
                      order: "desc"
                    }
                  ]}
                  pagination={paginationFactory(paginationOptions)}
                  noDataIndication={() => (
                    <div className="mpc-empty-state">
                      <i className="mdi mdi-account-off-outline" />
                      <p>No team members found for your role and filters.</p>
                    </div>
                  )}
                />
              </div>
            </>
          )}
        </ToolkitProvider>
      </div>
    </>
  );

  if (embedded) {
    return (
      <div className="mpc-tabs-shell mpc-team-performance-shell">
        <div className="mpc-tab-panel">
          <Card className="mpc-table-card mpc-table-card-embedded mpr-team-list-section">
            <CardBody className="mpc-tab-panel-body">{panelBody}</CardBody>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <Card className="mpc-table-card mpr-team-list-section">
      <CardBody>{panelBody}</CardBody>
    </Card>
  );
};

export default StaffPerformanceSection;
